import React from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { FiEye, FiPaperclip, FiTrash2, FiUpload } from "react-icons/fi";

import RequiredBadge from "@core/components/RequiredBadge";
import DepartmentReportUploadModal, {
  type UploadDraftRow,
} from "@core/reports/components/DepartmentReportUploadModal";
import type {
  DepartmentReportMediaItem,
  DepartmentReportDocument,
} from "@core/reports/api/types";
import {
  ACCEPTED_ATTACHMENT_EXTENSIONS,
  formatFileSize,
} from "@core/reports/constants/mediaAttachments";

type Props = {
  requirements: DepartmentReportDocument[];
  existingMedia: DepartmentReportMediaItem[];
  captionDrafts: Record<number, string>;
  mediaActionId: number | null;
  captionSavingId: number | null;
  error: string | null;
  /** Report is DRAFT / NEEDS_ATTENTION → allow upload, remove, and caption edit. */
  editable: boolean;
  /** Allow removing existing attachments (separate from upload). Defaults to `editable`. */
  allowRemove?: boolean;
  busy: boolean;
  uploadBusy: boolean;
  uploadDraft: {
    categoryLabel: string;
    rows: UploadDraftRow[];
  } | null;
  uploadError: string | null;
  namedFileInputRefs: React.MutableRefObject<Record<number, HTMLInputElement | null>>;
  otherFileInputRef: React.RefObject<HTMLInputElement | null>;
  onNamedFileChange: (requirementId: number, categoryLabel: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  onOtherFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUploadDraftCaptionChange: (index: number, caption: string) => void;
  onConfirmUpload: () => void;
  onCancelUpload: () => void;
  onCaptionDraftChange: (mediaStorageId: number, description: string) => void;
  onSaveCaption: (mediaStorageId: number) => void;
  onView: (media: DepartmentReportMediaItem) => void;
  onRemove: (media: DepartmentReportMediaItem) => void;
};

function formatUploadedAt(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function FileMeta({ media }: { media: DepartmentReportMediaItem }) {
  const when = formatUploadedAt(media.created_at);
  const who = media.uploaded_by;
  if (!who && !when) return null;
  return (
    <div className="text-muted small">
      {who ? `Uploaded by ${who}` : "Uploaded"}
      {when ? ` · ${when}` : ""}
    </div>
  );
}

/**
 * Named slots (one file each) plus an Other catch-all. File picks open a
 * confirmation modal; captions edit in place on existing files while editable.
 */
export default function DepartmentReportMediaPanel({
  requirements,
  existingMedia,
  captionDrafts,
  mediaActionId,
  captionSavingId,
  error,
  editable,
  allowRemove = editable,
  busy,
  uploadBusy,
  uploadDraft,
  uploadError,
  namedFileInputRefs,
  otherFileInputRef,
  onNamedFileChange,
  onOtherFileChange,
  onUploadDraftCaptionChange,
  onConfirmUpload,
  onCancelUpload,
  onCaptionDraftChange,
  onSaveCaption,
  onView,
  onRemove,
}: Props) {
  const actionBusy = busy || uploadBusy || mediaActionId !== null || captionSavingId !== null;
  const requirementIds = React.useMemo(
    () => new Set(requirements.map((slot) => slot.department_report_document_id)),
    [requirements]
  );
  const otherMedia = existingMedia.filter((item) => !item.department_report_document_id);
  const orphanNamedMedia = existingMedia.filter(
    (item) =>
      item.department_report_document_id != null &&
      !requirementIds.has(item.department_report_document_id)
  );
  const mediaBySlot = React.useMemo(() => {
    const map = new Map<number, DepartmentReportMediaItem>();
    for (const item of existingMedia) {
      if (item.department_report_document_id && requirementIds.has(item.department_report_document_id)) {
        map.set(item.department_report_document_id, item);
      }
    }
    return map;
  }, [existingMedia, requirementIds]);

  return (
    <div className="mt-4">
      <div className="mb-2">
        <div className="fw-semibold">Documents</div>
        <div className="text-muted small">
          Attach PDFs, Word, Excel, or image files. Maximum 10 MB per file. Each named document takes one file; Other can take many.
        </div>
      </div>

      {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

      {requirements.map((slot) => {
        const occupied = mediaBySlot.get(slot.department_report_document_id);
        return (
          <div
            key={slot.department_report_document_id}
            className="border rounded px-3 py-3 mb-3"
            data-testid={`media-slot-${slot.department_report_document_id}`}
          >
            <div
              className={
                occupied || !editable
                  ? "d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 mb-2"
                  : "d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2"
              }
            >
              <div>
                <div className="fw-medium">
                  {slot.name}
                  {slot.required_for_tag ? (
                    <RequiredBadge className="ms-2" />
                  ) : (
                    <Badge bg="light" text="dark" className="ms-2">Optional</Badge>
                  )}
                </div>
                {slot.description ? (
                  <div className="text-muted small">{slot.description}</div>
                ) : null}
              </div>
              {editable && !occupied ? (
                <>
                  <input
                    ref={(el: HTMLInputElement | null) => {
                      namedFileInputRefs.current[slot.department_report_document_id] = el;
                    }}
                    type="file"
                    accept={ACCEPTED_ATTACHMENT_EXTENSIONS}
                    className="d-none"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onNamedFileChange(slot.department_report_document_id, slot.name, event)
                    }
                    disabled={!editable || busy || uploadBusy}
                  />
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={() => namedFileInputRefs.current[slot.department_report_document_id]?.click()}
                    disabled={actionBusy}
                  >
                    <FiUpload className="me-2" aria-hidden />
                    Upload file
                  </Button>
                </>
              ) : null}
            </div>

            {occupied ? (
              <ExistingFileRow
                media={occupied}
                caption={captionDrafts[occupied.media_storage_id] ?? occupied.description ?? ""}
                editable={editable}
                allowRemove={allowRemove}
                actionBusy={actionBusy}
                mediaActionId={mediaActionId}
                captionSavingId={captionSavingId}
                onCaptionChange={onCaptionDraftChange}
                onSaveCaption={onSaveCaption}
                onView={onView}
                onRemove={onRemove}
              />
            ) : !editable ? (
              <div className="text-muted small">No file uploaded.</div>
            ) : null}
          </div>
        );
      })}

      {orphanNamedMedia.map((media) => (
        <div
          key={media.media_storage_id}
          className="border rounded px-3 py-3 mb-3"
          data-testid={`media-orphan-${media.media_storage_id}`}
        >
          <div className="fw-medium mb-2">{media.document_name || "Document"}</div>
          <ExistingFileRow
            media={media}
            caption={captionDrafts[media.media_storage_id] ?? media.description ?? ""}
            editable={false}
            allowRemove={allowRemove}
            actionBusy={actionBusy}
            mediaActionId={mediaActionId}
            captionSavingId={captionSavingId}
            onCaptionChange={onCaptionDraftChange}
            onSaveCaption={onSaveCaption}
            onView={onView}
            onRemove={onRemove}
          />
        </div>
      ))}

      <div className="border rounded px-3 py-3 mb-3" data-testid="media-slot-other">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 mb-2">
          <div>
            <div className="fw-medium">Other</div>
            <div className="text-muted small">Optional extras. You can attach more than one file.</div>
          </div>
          {editable ? (
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={() => otherFileInputRef.current?.click()}
              disabled={actionBusy}
            >
              <FiUpload className="me-2" aria-hidden />
              Add files
            </Button>
          ) : null}
        </div>
        <input
          ref={otherFileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_ATTACHMENT_EXTENSIONS}
          className="d-none"
          onChange={onOtherFileChange}
          disabled={!editable || busy || uploadBusy}
        />

        {otherMedia.map((media, index) => (
          <div
            key={media.media_storage_id}
            className={index > 0 ? "border-top pt-3 mt-2" : undefined}
          >
            <ExistingFileRow
              media={media}
              caption={captionDrafts[media.media_storage_id] ?? media.description ?? ""}
              editable={editable}
              allowRemove={allowRemove}
              actionBusy={actionBusy}
              mediaActionId={mediaActionId}
              captionSavingId={captionSavingId}
              onCaptionChange={onCaptionDraftChange}
              onSaveCaption={onSaveCaption}
              onView={onView}
              onRemove={onRemove}
            />
          </div>
        ))}

        {otherMedia.length === 0 ? (
          <div className="text-muted small">No other files attached yet.</div>
        ) : null}
      </div>

      <DepartmentReportUploadModal
        show={uploadDraft != null}
        categoryLabel={uploadDraft?.categoryLabel ?? ""}
        rows={uploadDraft?.rows ?? []}
        busy={uploadBusy}
        error={uploadError}
        onCaptionChange={onUploadDraftCaptionChange}
        onSave={onConfirmUpload}
        onCancel={onCancelUpload}
      />
    </div>
  );
}

function ExistingFileRow({
  media,
  caption,
  editable,
  allowRemove,
  actionBusy,
  mediaActionId,
  captionSavingId,
  onCaptionChange,
  onSaveCaption,
  onView,
  onRemove,
}: {
  media: DepartmentReportMediaItem;
  caption: string;
  editable: boolean;
  allowRemove: boolean;
  actionBusy: boolean;
  mediaActionId: number | null;
  captionSavingId: number | null;
  onCaptionChange: (mediaStorageId: number, description: string) => void;
  onSaveCaption: (mediaStorageId: number) => void;
  onView: (media: DepartmentReportMediaItem) => void;
  onRemove: (media: DepartmentReportMediaItem) => void;
}) {
  const captionId = `media-caption-${media.media_storage_id}`;
  const original = media.description ?? "";
  const dirty = caption !== original;
  const savingThis = captionSavingId === media.media_storage_id;

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="d-flex align-items-start gap-2 min-width-0">
          <FiPaperclip className="text-muted flex-shrink-0 mt-1" aria-hidden />
          <div className="min-width-0">
            <div className="fw-medium text-truncate">{media.filename || "Attachment"}</div>
            <div className="text-muted small">
              {formatFileSize(media.size_bytes)}
              {media.mime_type ? ` • ${media.mime_type}` : ""}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={() => onView(media)}
            disabled={mediaActionId !== null || captionSavingId !== null}
            title="View attachment"
            aria-label={`View ${media.filename || "attachment"}`}
          >
            {mediaActionId === media.media_storage_id ? (
              <Spinner animation="border" size="sm" aria-hidden />
            ) : (
              <FiEye aria-hidden />
            )}
          </Button>
          {allowRemove ? (
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={() => onRemove(media)}
              disabled={actionBusy}
              title="Remove attachment"
              aria-label={`Remove ${media.filename || "attachment"}`}
            >
              <FiTrash2 aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>

      {editable ? (
        <Form.Group controlId={captionId} className="d-flex align-items-center gap-2 mb-0">
          <Form.Label className="small mb-0 flex-shrink-0">Caption</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            maxLength={500}
            value={caption}
            placeholder="Add a short description (optional)"
            onChange={(event) => onCaptionChange(media.media_storage_id, event.target.value)}
            disabled={actionBusy}
            className="flex-grow-1"
          />
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            className="flex-shrink-0"
            onClick={() => onSaveCaption(media.media_storage_id)}
            disabled={actionBusy || !dirty}
          >
            {savingThis ? <Spinner animation="border" size="sm" className="me-2" aria-hidden /> : null}
            Save
          </Button>
        </Form.Group>
      ) : caption ? (
        <div className="text-muted small">{caption}</div>
      ) : null}

      <FileMeta media={media} />
    </div>
  );
}
